// RE₿EL by Eddy — UI strings (ES/EN/SK).
// Structure mirrors /i18n/shadow.js: one entry per language with the same
// key set. Consumed by RebelByEddyPage.jsx and RebelByEddyDashboardPage.jsx
// via useLanguage().lang → EDDY_I18N[lang].
//
// Tone guidelines followed:
//  - ES → warm, personal, Spanish gastronomy vibe (source of truth).
//  - EN → professional but casual, matches the "hospitality/chef" world.
//  - SK → concise Slovak; preserves brand tokens (RE₿EL, La Comisaría,
//    Sandals Resorts International) and Spanish dish names since they
//    are proper nouns.
//
// Long-form bio is intentionally translated in FULL — it is the primary
// storytelling content. The dish description on the demo post is kept in
// ES only (backend-seeded content, not part of i18n). If Eddy publishes
// new posts, each post's title/description live in the DB — those are not
// translated automatically.
export const EDDY_I18N = {
  es: {
    // Nav / menu
    nav_menu_item: "RE₿EL by Eddy",
    nav_badge_new: "NEW",

    // Hero
    hero_back: "RE₿EL",
    hero_title_prefix: "RE₿EL",
    hero_title_suffix: "by Eddy",
    hero_sub:
      "Edward Phillips Blanco. Chef ejecutivo, consultor culinario internacional y creador de experiencias gastronómicas que han pasado por Valencia, el Caribe y los hoteles más exigentes del mundo. Formado viajando, perfeccionado operando. Aquí comparte su visión sin filtros — desde su cocina hasta su mesa.",
    hero_dashboard_cta: "Ir al Dashboard",

    // Banner (foto de portada)
    banner_upload_change: "Cambiar portada",
    banner_upload_new: "Subir portada",
    banner_uploading: "Subiendo…",
    banner_error_default: "Error subiendo portada",

    // Biografía
    bio_section_title: "Biografía",
    bio_section_hint: "Historia · Filosofía · Proyectos",
    bio_header: "Historia de Eddy",
    bio_edit_btn: "Editar",
    bio_cancel_btn: "Cancelar",
    bio_save_btn: "Guardar",
    bio_photo_upload_change: "Cambiar foto",
    bio_photo_upload_new: "Subir foto",
    bio_photo_uploading: "Subiendo…",
    bio_placeholder:
      "Aquí Eddy contará su historia: cómo empezó, qué le llevó al mundo cripto, su trayectoria (La Comisaría Valencia, Sandals Resorts International, etc.), sus proyectos y su filosofía RE₿EL. (El admin de la sección puede editar este texto).",
    bio_error_max: "bio máximo 4000 caracteres",
    bio_error_save: "Error al guardar",
    bio_error_photo: "Error subiendo la foto",
    bio_loading: "Cargando…",

    // Publicaciones
    posts_section_title: "Publicaciones",
    posts_section_hint: "Feed exclusivo · canal aislado",
    posts_new_btn: "Nueva publicación",
    posts_close_btn: "Cerrar",
    posts_empty:
      "Aún no hay publicaciones. Vuelve pronto — Eddy está preparando la primera.",
    posts_load_more: "Cargar más",
    posts_comments_show: "Ver comentarios",
    posts_comments_hide: "Ocultar comentarios",
    posts_delete_confirm: "¿Eliminar esta publicación?",

    // Composer (post form)
    composer_title_ph: "Título",
    composer_ticker_ph: "Ticker (opcional) ej. BTC",
    composer_desc_ph:
      "Descripción — cuenta la historia detrás del setup, la reflexión, el aprendizaje…",
    composer_add_media: "Añadir imagen/vídeo",
    composer_change_media: "Cambiar archivo",
    composer_publish_btn: "Publicar",
    composer_err_validation:
      "Título ≥3, descripción ≥10 y una imagen/vídeo son obligatorios",
    composer_err_default: "Error al publicar",

    // Comments (inline panel)
    comments_input_authed_ph: "Escribe un comentario…",
    comments_input_guest_ph: "Inicia sesión para comentar",
    comments_send_btn: "Enviar",
    comments_first: "Sé el primero en comentar",
    comments_err_default: "Error al comentar",

    // Cocina en Vivo
    cocina_section_title: "Cocina en Vivo",
    cocina_section_hint: "Directo · YouTube",
    cocina_edit_btn: "Editar directo",
    cocina_url_label: "URL o ID de YouTube",
    cocina_url_ph: "https://youtu.be/… o dQw4w9WgXcQ",
    cocina_title_label: "Título del directo (opcional)",
    cocina_title_ph: "Miércoles 20h · Reacción al FOMC",
    cocina_save_btn: "Guardar",
    cocina_cancel_btn: "Cancelar",
    cocina_clear_btn: "Quitar",
    cocina_err_invalid: "URL inválida",
    cocina_empty:
      "Aún no hay directo configurado. Vuelve pronto para ver a Eddy en vivo.",
    cocina_iframe_fallback_title: "RE₿EL by Eddy — Cocina en Vivo",

    // Confirmations
    generic_delete_confirm: "¿Eliminar esta publicación?",
    delete_tooltip: "Eliminar",

    // EPB Global F&B (consultoría)
    epb_section_title: "EPB Global F&B",
    epb_section_hint: "Consultoría F&B · Alta dirección culinaria",
    epb_intro_default:
      "Consultoría Global: Edward Phillips Blanco ha enfocado su carrera hacia la alta gestión en el sector de alimentos y bebidas (F&B), desempeñándose como ejecutivo gastronómico global para firmas hoteleras internacionales de gran prestigio como Sandals Resorts International.",
    epb_services_heading: "Servicios",
    epb_services_default: [
      {
        title: "Diseño y desarrollo de conceptos culinarios",
        description: "Creación de propuestas gastronómicas diferenciadas para restaurantes, gastrobares y espacios F&B — desde la idea inicial hasta la carta final.",
        details: "Desarrollo integral de conceptos gastronómicos desde cero: definición de identidad culinaria, selección de producto, técnica y narrativa del espacio. Incluye estudio de posicionamiento, ficha técnica de platos, costeo de recetas y validación de viabilidad operativa antes de la apertura.",
      },
      {
        title: "Arquitectura de cartas y experiencia del comensal",
        description: "Diseño de cartas pensadas para maximizar rentabilidad, rotación y experiencia.",
        details: "Diseño estratégico de cartas: ingeniería de menú (menu engineering) para identificar platos estrella, optimizar márgenes y guiar la decisión del cliente. Incluye diseño de flujo de experiencia — desde el primer contacto visual con la carta hasta la elaboración en vivo frente al comensal.",
      },
      {
        title: "Gestión y liderazgo de equipos de cocina",
        description: "Formación, organización y estándares operativos para equipos culinarios en hoteles, cruceros y restaurantes independientes.",
        details: "Estructuración de brigadas de cocina: definición de roles, protocolos de trabajo, estándares de higiene y calidad, y planes de formación continua. Incluye acompañamiento en la selección de jefes de partida y desarrollo de cultura de equipo en entornos de alta exigencia (hoteles, cruceros, apertura de restaurantes).",
      },
      {
        title: "Consultoría para cadenas hoteleras de lujo y cruceros",
        description: "Auditoría y optimización de operaciones F&B a nivel ejecutivo.",
        details: "Auditoría ejecutiva de operaciones F&B a nivel de cadena o portafolio: revisión de rentabilidad por outlet, estandarización de procesos entre múltiples propiedades, y optimización de costos de food cost sin sacrificar experiencia premium. Basado en experiencia directa con firmas como Sandals Resorts International.",
      },
      {
        title: "Aperturas de nuevos conceptos gastronómicos",
        description: "Acompañamiento de ciclo completo en el lanzamiento de restaurantes y gastrobares.",
        details: "Acompañamiento de ciclo completo en la apertura de un nuevo restaurante o gastrobar: desde el concepto inicial y diseño de carta, hasta reclutamiento de equipo, montaje de cocina, pruebas de servicio y lanzamiento operativo.",
      },
    ],
    epb_contact_heading: "Solicita consultoría",
    epb_contact_intro: "Cuéntanos brevemente tu proyecto y Eddy te contactará personalmente.",
    epb_contact_name_ph: "Nombre completo",
    epb_contact_email_ph: "Email",
    epb_contact_company_ph: "Empresa (opcional)",
    epb_contact_message_ph: "Describe tu proyecto o consulta…",
    epb_contact_submit: "Enviar consulta",
    epb_contact_sending: "Enviando…",
    epb_contact_success: "Consulta enviada. Eddy se pondrá en contacto pronto.",
    epb_contact_err_required: "Completa nombre, email y mensaje",
    epb_contact_err_email: "Email inválido",
    epb_contact_err_default: "Error al enviar la consulta",
    // Accordion (expandable service cards)
    epb_service_expand: "Ver detalles",
    epb_service_collapse: "Ocultar detalles",

    // Bio default text (mostrado cuando profile.bio está vacío en la DB).
    // Contenido curado — biografía canónica de Edward Phillips Blanco.
    bio_default_text: `Edward Phillips Blanco es un reconocido chef y consultor culinario internacional conocido principalmente por su enfoque vanguardista en la cocina de fusión mediterránea y asiática. A lo largo de su carrera dentro del sector de la gastronomía, destaca por el desarrollo de conceptos gastronómicos y el liderazgo de equipos culinarios en cadenas de hoteles de lujo, cruceros y restaurantes independientes.

Orígenes: Creció viajando activamente debido a la profesión de su familia, lo que le permitió experimentar de primera mano una amplia gama de cocinas internacionales y desarrollar un paladar multicultural desde joven.

La Comisaría: Es especialmente recordado en el panorama nacional español por haber sido el chef principal y propietario de La Comisaría (Tapas y Copas Ilegales), un exitoso concepto de gastrobar ubicado en Valencia. Su propuesta se caracterizaba por platos creativos pensados para compartir al centro, fusionando técnicas orientales y productos mediterráneos, con elaboraciones a la vista del cliente desde la barra — como su icónico Chupa Chup de langostino rebozado con arroz inflado y alga wakame.

Consultoría Global: Posteriormente ha enfocado su carrera hacia la alta gestión en el sector de alimentos y bebidas (F&B), desempeñándose como ejecutivo gastronómico global para firmas hoteleras internacionales de gran prestigio como Sandals Resorts International.`,
  },

  en: {
    // Nav / menu
    nav_menu_item: "RE₿EL by Eddy",
    nav_badge_new: "NEW",

    // Hero
    hero_back: "RE₿EL",
    hero_title_prefix: "RE₿EL",
    hero_title_suffix: "by Eddy",
    hero_sub:
      "Edward Phillips Blanco. Executive chef, international culinary consultant and creator of gastronomic experiences across Valencia, the Caribbean and some of the world's most demanding hotels. Trained by traveling, refined by running kitchens. Here he shares his unfiltered vision — from his kitchen straight to your table.",
    hero_dashboard_cta: "Go to Dashboard",

    // Banner
    banner_upload_change: "Change cover",
    banner_upload_new: "Upload cover",
    banner_uploading: "Uploading…",
    banner_error_default: "Error uploading cover",

    // Biografía
    bio_section_title: "Biography",
    bio_section_hint: "Story · Philosophy · Projects",
    bio_header: "Eddy's story",
    bio_edit_btn: "Edit",
    bio_cancel_btn: "Cancel",
    bio_save_btn: "Save",
    bio_photo_upload_change: "Change photo",
    bio_photo_upload_new: "Upload photo",
    bio_photo_uploading: "Uploading…",
    bio_placeholder:
      "This is where Eddy will tell his story: how he started, what pulled him into crypto, his career path (La Comisaría Valencia, Sandals Resorts International, etc.), his projects and his RE₿EL philosophy. (The section admin can edit this text.)",
    bio_error_max: "Bio: max 4000 characters",
    bio_error_save: "Failed to save",
    bio_error_photo: "Error uploading the photo",
    bio_loading: "Loading…",

    // Publicaciones
    posts_section_title: "Posts",
    posts_section_hint: "Exclusive feed · isolated channel",
    posts_new_btn: "New post",
    posts_close_btn: "Close",
    posts_empty: "No posts yet. Come back soon — Eddy's cooking up the first one.",
    posts_load_more: "Load more",
    posts_comments_show: "View comments",
    posts_comments_hide: "Hide comments",
    posts_delete_confirm: "Delete this post?",

    // Composer
    composer_title_ph: "Title",
    composer_ticker_ph: "Ticker (optional) e.g. BTC",
    composer_desc_ph:
      "Description — tell the story behind the setup, the reflection, the lesson…",
    composer_add_media: "Add image/video",
    composer_change_media: "Change file",
    composer_publish_btn: "Publish",
    composer_err_validation:
      "Title ≥3, description ≥10 chars and an image/video are required",
    composer_err_default: "Failed to publish",

    // Comments
    comments_input_authed_ph: "Write a comment…",
    comments_input_guest_ph: "Sign in to comment",
    comments_send_btn: "Send",
    comments_first: "Be the first to comment",
    comments_err_default: "Failed to comment",

    // Cocina en Vivo
    cocina_section_title: "Live Cooking",
    cocina_section_hint: "Live stream · YouTube",
    cocina_edit_btn: "Edit stream",
    cocina_url_label: "YouTube URL or ID",
    cocina_url_ph: "https://youtu.be/… or dQw4w9WgXcQ",
    cocina_title_label: "Stream title (optional)",
    cocina_title_ph: "Wednesday 8pm · FOMC reaction",
    cocina_save_btn: "Save",
    cocina_cancel_btn: "Cancel",
    cocina_clear_btn: "Remove",
    cocina_err_invalid: "Invalid URL",
    cocina_empty:
      "No stream configured yet. Come back soon to catch Eddy live.",
    cocina_iframe_fallback_title: "RE₿EL by Eddy — Live Cooking",

    generic_delete_confirm: "Delete this post?",
    delete_tooltip: "Delete",

    // EPB Global F&B (consulting)
    epb_section_title: "EPB Global F&B",
    epb_section_hint: "F&B consulting · Senior culinary leadership",
    epb_intro_default:
      "Global Consulting: Edward Phillips Blanco has focused his professional career on senior food & beverage (F&B) management, serving as a global culinary executive for prestigious international hotel groups such as Sandals Resorts International.",
    epb_services_heading: "Services",
    epb_services_default: [
      {
        title: "Culinary concept design & development",
        description: "Crafting differentiated culinary proposals for restaurants, gastrobars and F&B venues — from initial idea to final menu.",
        details: "Comprehensive development of gastronomic concepts from scratch: definition of culinary identity, product selection, technique and space narrative. Includes positioning study, dish technical sheets, recipe costing and operational viability validation before opening.",
      },
      {
        title: "Menu architecture & guest experience",
        description: "Designing menus engineered to maximize profitability, product rotation and experience.",
        details: "Strategic menu design: menu engineering to identify star dishes, optimize margins and guide customer decision-making. Includes experience flow design — from the first visual contact with the menu to live preparation in front of the diner.",
      },
      {
        title: "Kitchen team management & leadership",
        description: "Training, org design and operational standards for culinary teams across hotels, cruises and independent restaurants.",
        details: "Kitchen brigade structuring: definition of roles, work protocols, hygiene and quality standards, and continuous training plans. Includes support in the selection of station chefs and development of team culture in high-demand environments (hotels, cruise ships, restaurant openings).",
      },
      {
        title: "Consulting for luxury hotel chains & cruise lines",
        description: "Executive-level audits and optimization of F&B operations.",
        details: "Executive audit of F&B operations at chain or portfolio level: profitability review by outlet, process standardization across multiple properties, and food cost optimization without sacrificing premium experience. Based on direct experience with firms such as Sandals Resorts International.",
      },
      {
        title: "New culinary concept openings",
        description: "Full-cycle support in the launch of restaurants and gastrobars.",
        details: "Full-cycle support in opening a new restaurant or gastrobar: from initial concept and menu design, to team recruitment, kitchen setup, service trials, and operational launch.",
      },
    ],
    epb_contact_heading: "Request consulting",
    epb_contact_intro: "Tell us briefly about your project and Eddy will get back to you personally.",
    epb_contact_name_ph: "Full name",
    epb_contact_email_ph: "Email",
    epb_contact_company_ph: "Company (optional)",
    epb_contact_message_ph: "Describe your project or inquiry…",
    epb_contact_submit: "Send inquiry",
    epb_contact_sending: "Sending…",
    epb_contact_success: "Inquiry sent. Eddy will be in touch soon.",
    epb_contact_err_required: "Fill in name, email and message",
    epb_contact_err_email: "Invalid email",
    epb_contact_err_default: "Failed to send inquiry",
    // Accordion (expandable service cards)
    epb_service_expand: "See details",
    epb_service_collapse: "Hide details",

    bio_default_text: `Edward Phillips Blanco is a renowned chef and international culinary consultant, best known for his cutting-edge take on Mediterranean-Asian fusion cuisine. Throughout his career in gastronomy he stands out for developing culinary concepts and leading kitchen teams across luxury hotel chains, cruise lines and independent restaurants.

Origins: He grew up traveling extensively thanks to his family's profession, which let him experience a wide range of international cuisines first-hand and develop a multicultural palate from a young age.

La Comisaría: He is especially remembered on the Spanish national scene as the head chef and owner of La Comisaría (Tapas y Copas Ilegales), a wildly successful gastrobar concept in Valencia. His proposal centered on creative sharing plates at the center of the table, blending Asian techniques with Mediterranean produce, with dishes finished in front of the guest right at the counter — like his iconic "Chupa Chup" of tempura prawn coated with puffed rice and wakame seaweed.

Global Consulting: He later focused his career on senior food & beverage (F&B) management, serving as a global culinary executive for prestigious international hotel groups such as Sandals Resorts International.`,
  },

  sk: {
    // Nav / menu
    nav_menu_item: "RE₿EL by Eddy",
    nav_badge_new: "NOVÉ",

    // Hero
    hero_back: "RE₿EL",
    hero_title_prefix: "RE₿EL",
    hero_title_suffix: "by Eddy",
    hero_sub:
      "Edward Phillips Blanco. Šéfkuchár, medzinárodný kulinársky konzultant a tvorca gastronomických zážitkov, ktoré prešli Valenciou, Karibikom a najnáročnejšími hotelmi sveta. Vyškolený cestovaním, vybrúsený praxou. Tu zdieľa svoju víziu bez filtra — priamo zo svojej kuchyne na váš stôl.",
    hero_dashboard_cta: "Prejsť na Dashboard",

    // Banner
    banner_upload_change: "Zmeniť obálku",
    banner_upload_new: "Nahrať obálku",
    banner_uploading: "Nahrávam…",
    banner_error_default: "Chyba pri nahrávaní obálky",

    // Biografía
    bio_section_title: "Biografia",
    bio_section_hint: "Príbeh · Filozofia · Projekty",
    bio_header: "Eddyho príbeh",
    bio_edit_btn: "Upraviť",
    bio_cancel_btn: "Zrušiť",
    bio_save_btn: "Uložiť",
    bio_photo_upload_change: "Zmeniť fotku",
    bio_photo_upload_new: "Nahrať fotku",
    bio_photo_uploading: "Nahrávam…",
    bio_placeholder:
      "Tu Eddy porozpráva svoj príbeh: ako začínal, čo ho pritiahlo do sveta krypta, jeho kariéru (La Comisaría Valencia, Sandals Resorts International, atď.), projekty a RE₿EL filozofiu. (Administrátor sekcie môže tento text upraviť.)",
    bio_error_max: "Bio: max 4000 znakov",
    bio_error_save: "Nepodarilo sa uložiť",
    bio_error_photo: "Chyba pri nahrávaní fotky",
    bio_loading: "Načítavam…",

    // Publicaciones
    posts_section_title: "Príspevky",
    posts_section_hint: "Exkluzívny feed · izolovaný kanál",
    posts_new_btn: "Nový príspevok",
    posts_close_btn: "Zavrieť",
    posts_empty:
      "Zatiaľ žiadne príspevky. Vráť sa čoskoro — Eddy pripravuje prvý.",
    posts_load_more: "Načítať viac",
    posts_comments_show: "Zobraziť komentáre",
    posts_comments_hide: "Skryť komentáre",
    posts_delete_confirm: "Odstrániť tento príspevok?",

    // Composer
    composer_title_ph: "Názov",
    composer_ticker_ph: "Ticker (voliteľné) napr. BTC",
    composer_desc_ph:
      "Popis — povedz príbeh za setupom, úvahu, ponaučenie…",
    composer_add_media: "Pridať obrázok/video",
    composer_change_media: "Zmeniť súbor",
    composer_publish_btn: "Zverejniť",
    composer_err_validation:
      "Názov ≥3 znaky, popis ≥10 znakov a obrázok/video sú povinné",
    composer_err_default: "Zverejnenie zlyhalo",

    // Comments
    comments_input_authed_ph: "Napíš komentár…",
    comments_input_guest_ph: "Prihlás sa a komentuj",
    comments_send_btn: "Odoslať",
    comments_first: "Buď prvý, kto komentuje",
    comments_err_default: "Komentár sa nepodaril",

    // Cocina en Vivo
    cocina_section_title: "Živé varenie",
    cocina_section_hint: "Live stream · YouTube",
    cocina_edit_btn: "Upraviť stream",
    cocina_url_label: "YouTube URL alebo ID",
    cocina_url_ph: "https://youtu.be/… alebo dQw4w9WgXcQ",
    cocina_title_label: "Názov streamu (voliteľné)",
    cocina_title_ph: "Streda 20:00 · Reakcia na FOMC",
    cocina_save_btn: "Uložiť",
    cocina_cancel_btn: "Zrušiť",
    cocina_clear_btn: "Odstrániť",
    cocina_err_invalid: "Neplatná URL",
    cocina_empty:
      "Zatiaľ nie je nastavený žiadny stream. Vráť sa čoskoro na Eddyho naživo.",
    cocina_iframe_fallback_title: "RE₿EL by Eddy — Živé varenie",

    generic_delete_confirm: "Odstrániť tento príspevok?",
    delete_tooltip: "Zmazať",

    // EPB Global F&B (konzultácie)
    epb_section_title: "EPB Global F&B",
    epb_section_hint: "F&B konzultácie · Vrcholový gastro-manažment",
    epb_intro_default:
      "Globálna konzultácia: Edward Phillips Blanco zameral svoju profesionálnu kariéru na vrcholový manažment v sektore potravín a nápojov (F&B), pôsobiac ako globálny kulinársky výkonný riaditeľ pre prestížne medzinárodné hotelové skupiny ako Sandals Resorts International.",
    epb_services_heading: "Služby",
    epb_services_default: [
      {
        title: "Návrh a vývoj gastronomických konceptov",
        description: "Tvorba diferencovaných kulinárskych ponúk pre reštaurácie, gastrobary a F&B priestory — od nápadu po finálne menu.",
        details: "Komplexný vývoj gastronomických konceptov od nuly: definovanie kulinárskej identity, výber produktov, technika a naratív priestoru. Zahŕňa štúdiu positioningu, technické karty jedál, kalkuláciu receptúr a validáciu prevádzkovej realizovateľnosti pred otvorením.",
      },
      {
        title: "Architektúra menu a zážitok hosťa",
        description: "Návrh menu s cieľom maximalizovať ziskovosť, rotáciu produktov a zážitok.",
        details: "Strategický dizajn jedálnych lístkov: menu engineering na identifikáciu hviezdnych jedál, optimalizáciu marží a vedenie rozhodnutia klienta. Zahŕňa dizajn toku zážitku — od prvého vizuálneho kontaktu s jedálnym lístkom až po prípravu naživo pred hosťom.",
      },
      {
        title: "Manažment a vedenie kuchynských tímov",
        description: "Školenie, organizačná štruktúra a prevádzkové štandardy pre kulinárske tímy v hoteloch, na výletných lodiach a v nezávislých reštauráciách.",
        details: "Štruktúrovanie kuchynských brigád: definícia rolí, pracovné protokoly, štandardy hygieny a kvality a plány priebežného vzdelávania. Zahŕňa sprevádzanie pri výbere šéfov úsekov a rozvoj tímovej kultúry v náročných prostrediach (hotely, výletné lode, otváranie reštaurácií).",
      },
      {
        title: "Konzultácie pre luxusné hotelové reťazce a výletné lode",
        description: "Audit a optimalizácia F&B operácií na výkonnej úrovni.",
        details: "Výkonný audit prevádzky F&B na úrovni reťazca alebo portfólia: preskúmanie ziskovosti jednotlivých prevádzok, štandardizácia procesov naprieč viacerými nehnuteľnosťami a optimalizácia nákladov na potraviny bez obetovania prémiového zážitku. Založené na priamych skúsenostiach s firmami ako Sandals Resorts International.",
      },
      {
        title: "Otvorenie nových gastronomických konceptov",
        description: "Komplexná podpora pri spustení reštaurácií a gastrobarov.",
        details: "Komplexné sprevádzanie pri otvorení novej reštaurácie alebo gastrobaru: od počiatočného konceptu a dizajnu menu, cez nábor tímu, montáž kuchyne, skúšobné servisy až po operatívne spustenie.",
      },
    ],
    epb_contact_heading: "Žiadosť o konzultáciu",
    epb_contact_intro: "Napíš nám stručne o svojom projekte a Eddy sa ti osobne ozve.",
    epb_contact_name_ph: "Meno a priezvisko",
    epb_contact_email_ph: "Email",
    epb_contact_company_ph: "Spoločnosť (voliteľné)",
    epb_contact_message_ph: "Opíš svoj projekt alebo otázku…",
    epb_contact_submit: "Odoslať žiadosť",
    epb_contact_sending: "Odosielam…",
    epb_contact_success: "Žiadosť odoslaná. Eddy sa ti ozve čoskoro.",
    epb_contact_err_required: "Vyplň meno, email a správu",
    epb_contact_err_email: "Neplatný email",
    epb_contact_err_default: "Odoslanie zlyhalo",
    // Accordion (expandable service cards)
    epb_service_expand: "Zobraziť detaily",
    epb_service_collapse: "Skryť detaily",

    bio_default_text: `Edward Phillips Blanco je uznávaný šéfkuchár a medzinárodný kulinársky konzultant, známy predovšetkým svojím inovatívnym prístupom k stredomorsko-ázijskej fusion kuchyni. Počas svojej kariéry v gastronómii vyniká vývojom gastronomických konceptov a vedením kuchynských tímov v luxusných hotelových reťazcoch, na výletných lodiach a v nezávislých reštauráciách.

Pôvod: Vyrastal aktívnym cestovaním vďaka povolaniu svojej rodiny, čo mu umožnilo z prvej ruky zažiť širokú škálu medzinárodných kuchýň a už od mladosti si vyvinúť multikultúrne chuťové pohárikové vnímanie.

La Comisaría: Zvlášť sa naňho spomína v španielskom národnom kulinárskom prostredí ako na hlavného šéfkuchára a majiteľa La Comisaría (Tapas y Copas Ilegales), úspešného konceptu gastrobaru vo Valencii. Jeho ponuka sa vyznačovala kreatívnymi jedlami určenými na zdieľanie v strede stola, ktoré spájali ázijské techniky so stredomorskými produktmi, pripravovanými priamo pred hosťom pri bare — ako jeho ikonický "Chupa Chup" z tempura krevety obalenej v pufovanej ryži a wakame riase.

Globálna konzultácia: Neskôr sa zameral na vysoký manažment v sektore potravín a nápojov (F&B), pôsobiac ako globálny kulinársky výkonný riaditeľ pre prestížne medzinárodné hotelové skupiny ako Sandals Resorts International.`,
  },

  "zh-CN": {
    nav_menu_item: "RE₿EL by Eddy",
    nav_badge_new: "NEW",
    hero_back: "RE₿EL",
    hero_title_prefix: "RE₿EL",
    hero_title_suffix: "by Eddy",
    hero_sub: "Edward Phillips Blanco。行政总厨、国际烹饪顾问和美食体验创造者,足迹遍布瓦伦西亚、加勒比海和全球最顶级的酒店。在旅行中成长,在运营中精进。在这里分享他不加修饰的视角——从他的厨房到你的餐桌。",
    hero_dashboard_cta: "进入控制台",
    banner_upload_change: "更换封面",
    banner_upload_new: "上传封面",
    banner_uploading: "上传中…",
    banner_error_default: "上传封面时出错",
    bio_section_title: "个人简介",
    bio_section_hint: "故事 · 理念 · 项目",
    bio_header: "Eddy 的故事",
    bio_edit_btn: "编辑",
    bio_cancel_btn: "取消",
    bio_save_btn: "保存",
    bio_photo_upload_change: "更换照片",
    bio_photo_upload_new: "上传照片",
    bio_photo_uploading: "上传中…",
    bio_placeholder: "Eddy 将在这里讲述他的故事:他如何开始,是什么把他带入加密世界,他的职业轨迹(La Comisaría Valencia、Sandals Resorts International 等),他的项目以及他的 RE₿EL 理念。(本区域的管理员可以编辑此文本)。",
    bio_error_max: "个人简介最多4000字符",
    bio_error_save: "保存时出错",
    bio_error_photo: "上传照片时出错",
    bio_loading: "加载中…",
    posts_section_title: "发布内容",
    posts_section_hint: "独家动态 · 独立频道",
    posts_new_btn: "新发布",
    posts_close_btn: "关闭",
    posts_empty: "暂无发布内容。敬请期待——Eddy 正在准备第一篇。",
    posts_load_more: "加载更多",
    posts_comments_show: "查看评论",
    posts_comments_hide: "隐藏评论",
    posts_delete_confirm: "删除这篇发布内容?",
    composer_title_ph: "标题",
    composer_ticker_ph: "代币符号(可选)例如 BTC",
    composer_desc_ph: "描述——讲述布局背后的故事、思考、学习心得…",
    composer_add_media: "添加图片/视频",
    composer_change_media: "更换文件",
    composer_publish_btn: "发布",
    composer_err_validation: "标题≥3字符,描述≥10字符,且必须包含一张图片/视频",
    composer_err_default: "发布时出错",
    comments_input_authed_ph: "写下评论…",
    comments_input_guest_ph: "登录后评论",
    comments_send_btn: "发送",
    comments_first: "成为第一个评论的人",
    comments_err_default: "评论时出错",
    cocina_section_title: "现场直播",
    cocina_section_hint: "直播 · YouTube",
    cocina_edit_btn: "编辑直播",
    cocina_url_label: "YouTube URL 或 ID",
    cocina_url_ph: "https://youtu.be/… 或 dQw4w9WgXcQ",
    cocina_title_label: "直播标题(可选)",
    cocina_title_ph: "周三 20:00 · FOMC 会议反应",
    cocina_save_btn: "保存",
    cocina_cancel_btn: "取消",
    cocina_clear_btn: "移除",
    cocina_err_invalid: "URL 无效",
    cocina_empty: "暂未配置直播。敬请期待,即将看到 Eddy 的现场直播。",
    cocina_iframe_fallback_title: "RE₿EL by Eddy — 现场直播",
    generic_delete_confirm: "删除这篇发布内容?",
    delete_tooltip: "删除",
    epb_section_title: "EPB Global F&B",
    epb_section_hint: "F&B 咨询 · 高级烹饪管理",
    epb_intro_default: "全球咨询:Edward Phillips Blanco 的职业生涯专注于餐饮(F&B)领域的高级管理,担任国际知名酒店集团如 Sandals Resorts International 的全球美食执行官。",
    epb_services_heading: "服务",
    epb_services_default: [
      {
        title: "烹饪概念设计与开发",
        description: "为餐厅、美食酒吧和 F&B 空间创建差异化的美食提案——从初始创意到最终菜单。",
        details: "从零开始全面开发美食概念:定义烹饪身份、产品选择、技术和空间叙事。包括定位研究、菜品技术规格、食谱成本核算以及开业前的运营可行性验证。",
      },
      {
        title: "菜单架构与用餐体验",
        description: "设计菜单以最大化盈利能力、周转率和体验。",
        details: "战略性菜单设计:菜单工程(menu engineering)以识别明星菜品、优化利润率并引导顾客决策。包括体验流程设计——从顾客首次视觉接触菜单到在顾客面前现场制作。",
      },
      {
        title: "厨房团队管理与领导",
        description: "为酒店、邮轮和独立餐厅的烹饪团队提供培训、组织和运营标准。",
        details: "厨房团队结构化:角色定义、工作流程、卫生与质量标准,以及持续培训计划。包括协助选拔部门主管,并在高要求环境(酒店、邮轮、餐厅开业)中发展团队文化。",
      },
      {
        title: "豪华连锁酒店和邮轮咨询",
        description: "在执行层面对 F&B 运营进行审计和优化。",
        details: "连锁或组合级别的 F&B 运营执行审计:审查各门店盈利能力、多物业间流程标准化,以及在不牺牲高端体验的前提下优化食品成本。基于与 Sandals Resorts International 等公司的直接经验。",
      },
      {
        title: "新美食概念开业",
        description: "全周期陪同餐厅和美食酒吧的启动。",
        details: "全周期陪同新餐厅或美食酒吧的开业:从初始概念和菜单设计,到团队招聘、厨房布置、服务测试和运营启动。",
      }
    ],
    epb_contact_heading: "申请咨询",
    epb_contact_intro: "简要介绍你的项目,Eddy 将亲自与你联系。",
    epb_contact_name_ph: "全名",
    epb_contact_email_ph: "Email",
    epb_contact_company_ph: "公司(可选)",
    epb_contact_message_ph: "描述你的项目或咨询需求…",
    epb_contact_submit: "提交咨询",
    epb_contact_sending: "发送中…",
    epb_contact_success: "咨询已发送。Eddy 将很快与你联系。",
    epb_contact_err_required: "请填写姓名、email 和留言",
    epb_contact_err_email: "Email 无效",
    epb_contact_err_default: "发送咨询时出错",
    epb_service_expand: "查看详情",
    epb_service_collapse: "隐藏详情",
    bio_default_text: "Edward Phillips Blanco 是一位享有盛誉的厨师和国际烹饪顾问,主要以其在地中海与亚洲融合料理方面的前卫理念而闻名。在他的美食职业生涯中,他以开发美食概念和领导豪华连锁酒店、邮轮和独立餐厅的烹饪团队而著称。\n\n起源:由于家庭职业的关系,他在不断旅行中成长,这使他能够亲身体验各种国际美食,并从小培养出多元文化的味觉。\n\nLa Comisaría:在西班牙国内美食界,他尤其以担任 La Comisaría(Tapas y Copas Ilegales)的主厨兼所有者而被铭记,这是位于瓦伦西亚的一家成功的美食酒吧概念店。他的特色是创意菜品,设计用于中心共享,融合东方技术与地中海食材,并从吧台直接在顾客面前现场制作——例如他标志性的裹着膨化米饭和海带的油炸虾棒棒糖。\n\n全球咨询:此后,他的职业生涯专注于餐饮(F&B)领域的高级管理,担任国际知名酒店集团如 Sandals Resorts International 的全球美食执行官。",
  },

  de: {
    nav_menu_item: "RE₿EL by Eddy",
    nav_badge_new: "NEU",
    hero_back: "RE₿EL",
    hero_title_prefix: "RE₿EL",
    hero_title_suffix: "by Eddy",
    hero_sub: "Edward Phillips Blanco. Küchenchef, internationaler kulinarischer Berater und Schöpfer gastronomischer Erlebnisse, die durch Valencia, die Karibik und die anspruchsvollsten Hotels der Welt führten. Auf Reisen ausgebildet, im Betrieb perfektioniert. Hier teilt er seine ungefilterte Vision — von seiner Küche bis zu seinem Tisch.",
    hero_dashboard_cta: "Zum Dashboard",
    banner_upload_change: "Banner ändern",
    banner_upload_new: "Banner hochladen",
    banner_uploading: "Wird hochgeladen…",
    banner_error_default: "Fehler beim Hochladen des Banners",
    bio_section_title: "Biografie",
    bio_section_hint: "Geschichte · Philosophie · Projekte",
    bio_header: "Eddys Geschichte",
    bio_edit_btn: "Bearbeiten",
    bio_cancel_btn: "Abbrechen",
    bio_save_btn: "Speichern",
    bio_photo_upload_change: "Foto ändern",
    bio_photo_upload_new: "Foto hochladen",
    bio_photo_uploading: "Wird hochgeladen…",
    bio_placeholder: "Hier wird Eddy seine Geschichte erzählen: wie er anfing, was ihn in die Kryptowelt führte, seine Laufbahn (La Comisaría Valencia, Sandals Resorts International usw.), seine Projekte und seine RE₿EL-Philosophie. (Der Admin dieses Bereichs kann diesen Text bearbeiten).",
    bio_error_max: "Bio maximal 4000 Zeichen",
    bio_error_save: "Fehler beim Speichern",
    bio_error_photo: "Fehler beim Hochladen des Fotos",
    bio_loading: "Wird geladen…",
    posts_section_title: "Beiträge",
    posts_section_hint: "Exklusiver Feed · isolierter Kanal",
    posts_new_btn: "Neuer Beitrag",
    posts_close_btn: "Schließen",
    posts_empty: "Noch keine Beiträge. Komm bald wieder — Eddy bereitet den ersten vor.",
    posts_load_more: "Mehr laden",
    posts_comments_show: "Kommentare anzeigen",
    posts_comments_hide: "Kommentare verbergen",
    posts_delete_confirm: "Diesen Beitrag löschen?",
    composer_title_ph: "Titel",
    composer_ticker_ph: "Ticker (optional) z.B. BTC",
    composer_desc_ph: "Beschreibung — erzähle die Geschichte hinter dem Setup, die Überlegung, die Erkenntnis…",
    composer_add_media: "Bild/Video hinzufügen",
    composer_change_media: "Datei ändern",
    composer_publish_btn: "Veröffentlichen",
    composer_err_validation: "Titel ≥3, Beschreibung ≥10 und ein Bild/Video sind erforderlich",
    composer_err_default: "Fehler beim Veröffentlichen",
    comments_input_authed_ph: "Schreibe einen Kommentar…",
    comments_input_guest_ph: "Melde dich an, um zu kommentieren",
    comments_send_btn: "Senden",
    comments_first: "Sei der Erste, der kommentiert",
    comments_err_default: "Fehler beim Kommentieren",
    cocina_section_title: "Live-Küche",
    cocina_section_hint: "Livestream · YouTube",
    cocina_edit_btn: "Livestream bearbeiten",
    cocina_url_label: "YouTube-URL oder -ID",
    cocina_url_ph: "https://youtu.be/… oder dQw4w9WgXcQ",
    cocina_title_label: "Livestream-Titel (optional)",
    cocina_title_ph: "Mittwoch 20 Uhr · Reaktion auf FOMC",
    cocina_save_btn: "Speichern",
    cocina_cancel_btn: "Abbrechen",
    cocina_clear_btn: "Entfernen",
    cocina_err_invalid: "Ungültige URL",
    cocina_empty: "Noch kein Livestream konfiguriert. Komm bald wieder, um Eddy live zu sehen.",
    cocina_iframe_fallback_title: "RE₿EL by Eddy — Live-Küche",
    generic_delete_confirm: "Diesen Beitrag löschen?",
    delete_tooltip: "Löschen",
    epb_section_title: "EPB Global F&B",
    epb_section_hint: "F&B-Beratung · Kulinarisches Top-Management",
    epb_intro_default: "Globale Beratung: Edward Phillips Blanco hat seine Karriere auf das Top-Management im Food & Beverage (F&B)-Sektor ausgerichtet und war als globaler kulinarischer Geschäftsführer für renommierte internationale Hotelketten wie Sandals Resorts International tätig.",
    epb_services_heading: "Dienstleistungen",
    epb_services_default: [
      {
        title: "Design und Entwicklung kulinarischer Konzepte",
        description: "Entwicklung differenzierter gastronomischer Konzepte für Restaurants, Gastrobars und F&B-Spaces — von der ersten Idee bis zur finalen Karte.",
        details: "Integrale Entwicklung gastronomischer Konzepte von Grund auf: Definition kulinarischer Identität, Produktauswahl, Technik und Narrativ des Raums. Umfasst Positionierungsstudie, technische Datenkarten der Gerichte, Rezeptkalkulation und Validierung der operativen Machbarkeit vor der Eröffnung.",
      },
      {
        title: "Kartenarchitektur und Gästeerlebnis",
        description: "Kartendesign zur Maximierung von Rentabilität, Rotation und Erlebnis.",
        details: "Strategisches Kartendesign: Menu Engineering zur Identifizierung von Star-Gerichten, Optimierung der Margen und Steuerung der Gästeentscheidung. Umfasst Design des Erlebnisflusses — vom ersten visuellen Kontakt mit der Karte bis zur Live-Zubereitung vor dem Gast.",
      },
      {
        title: "Management und Führung von Küchenteams",
        description: "Schulung, Organisation und operative Standards für kulinarische Teams in Hotels, Kreuzfahrtschiffen und unabhängigen Restaurants.",
        details: "Strukturierung von Küchenbrigaden: Definition von Rollen, Arbeitsprotokollen, Hygiene- und Qualitätsstandards sowie kontinuierliche Schulungspläne. Umfasst Begleitung bei der Auswahl von Partieführern und Entwicklung einer Teamkultur in anspruchsvollen Umgebungen (Hotels, Kreuzfahrtschiffe, Restauranteröffnungen).",
      },
      {
        title: "Beratung für Luxushotelketten und Kreuzfahrtschiffe",
        description: "Audit und Optimierung von F&B-Betrieben auf Geschäftsführungsebene.",
        details: "Geschäftsführungs-Audit von F&B-Betrieben auf Ketten- oder Portfolioebene: Überprüfung der Rentabilität pro Outlet, Standardisierung von Prozessen über mehrere Standorte hinweg und Optimierung der Food Costs ohne Kompromisse beim Premium-Erlebnis. Basierend auf direkter Erfahrung mit Firmen wie Sandals Resorts International.",
      },
      {
        title: "Eröffnungen neuer gastronomischer Konzepte",
        description: "Vollständige Begleitung beim Launch von Restaurants und Gastrobars.",
        details: "Vollständige Begleitung bei der Eröffnung eines neuen Restaurants oder einer Gastrobar: vom ersten Konzept und Kartendesign über Teamrekrutierung, Küchenaufbau, Servicetests bis zum operativen Launch.",
      }
    ],
    epb_contact_heading: "Beratung anfragen",
    epb_contact_intro: "Erzähle uns kurz über dein Projekt und Eddy wird sich persönlich bei dir melden.",
    epb_contact_name_ph: "Vollständiger Name",
    epb_contact_email_ph: "E-Mail",
    epb_contact_company_ph: "Unternehmen (optional)",
    epb_contact_message_ph: "Beschreibe dein Projekt oder deine Anfrage…",
    epb_contact_submit: "Anfrage senden",
    epb_contact_sending: "Wird gesendet…",
    epb_contact_success: "Anfrage gesendet. Eddy wird sich bald melden.",
    epb_contact_err_required: "Fülle Name, E-Mail und Nachricht aus",
    epb_contact_err_email: "Ungültige E-Mail",
    epb_contact_err_default: "Fehler beim Senden der Anfrage",
    epb_service_expand: "Details anzeigen",
    epb_service_collapse: "Details verbergen",
    bio_default_text: "Edward Phillips Blanco ist ein renommierter Koch und internationaler kulinarischer Berater, der vor allem für seinen avantgardistischen Ansatz in der mediterranen und asiatischen Fusionsküche bekannt ist. Im Laufe seiner Karriere im Gastronomiesektor zeichnet er sich durch die Entwicklung gastronomischer Konzepte und die Führung kulinarischer Teams in Luxushotelketten, Kreuzfahrtschiffen und unabhängigen Restaurants aus.\n\nUrsprünge: Er wuchs aufgrund des Berufs seiner Familie aktiv reisend auf, was es ihm ermöglichte, aus erster Hand eine breite Palette internationaler Küchen zu erleben und von jungen Jahren an einen multikulturellen Gaumen zu entwickeln.\n\nLa Comisaría: Er wird im spanischen nationalen Panorama besonders als Küchenchef und Eigentümer von La Comisaría (Tapas y Copas Ilegales) in Erinnerung behalten, einem erfolgreichen Gastrobar-Konzept in Valencia. Sein Konzept zeichnete sich durch kreative Gerichte zum Teilen in der Mitte aus, die orientalische Techniken und mediterrane Produkte verschmelzen und von der Theke aus vor den Augen der Gäste zubereitet werden — wie sein ikonischer Chupa Chup aus paniertem Langustino mit gepufftem Reis und Wakame-Algen.\n\nGlobale Beratung: Anschließend hat er seine Karriere auf das Top-Management im Food & Beverage (F&B)-Sektor ausgerichtet und war als globaler kulinarischer Geschäftsführer für renommierte internationale Hotelketten wie Sandals Resorts International tätig.",
  },
};
